import {type FC, type RefObject, useEffect} from 'react';
import CenterWait from '../../../assets/broadcast/center-wait.jpg';
import useCenterStore from '../../../store/useCenterStore.ts';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import type {JitsiTrackState} from '../types/lib-jitsi-meet';
import type {ILayOut} from '../../../utils/StreamingUtil.ts';

interface LocalCellProps {
    localElemRef: RefObject<HTMLVideoElement | null>;
    localTrack: RefObject<JitsiTrackState>;
    localTrackVersion: number;
    gridInfo: ILayOut;
    isIsland?: boolean;
}

const LocalCell: FC<LocalCellProps> = ({localElemRef, localTrack, localTrackVersion, gridInfo, isIsland = false}) => {
    const isMicMuted = useStreamingStore((s) => s.isMicMuted);
    // 라벨의 출처는 centerInfo 하나뿐이다. Jitsi 토큰에서 읽은 이름은 발급 요청에 보낸
    // 경로당 코드(ICS-000)가 되돌아온 것이라 화면에 쓰면 안 된다.
    const centerName = useCenterStore((s) => s.centerInfo?.name ?? '');

    useEffect(() => {
        const currentVideoTrack = localTrack.current?.videoTrack;
        const currentAudioTrack = localTrack.current?.audioTrack;
        const videoElement = localElemRef.current;
        const audioElement = document.getElementById('local-cell-audio');
        return () => {
            if (videoElement && currentVideoTrack) currentVideoTrack.detach(videoElement);
            if (audioElement && currentAudioTrack) currentAudioTrack.detach(audioElement);
        };
    }, [localTrack.current?.videoTrack, localTrack.current?.audioTrack, localTrackVersion]);

    useEffect(() => {
        setTimeout(() => {
            const currentVideoTrack = localTrack.current?.videoTrack;
            const currentAudioTrack = localTrack.current?.audioTrack;
            const videoElement = localElemRef.current;
            const audioElement = document.getElementById('local-cell-audio');
            if (currentVideoTrack && videoElement) currentVideoTrack.attach(videoElement);
            if (currentAudioTrack && audioElement) currentAudioTrack.attach(audioElement);
        }, 1000);
    }, [localTrack.current?.videoTrack, localTrack.current?.audioTrack, localTrackVersion]);

    const containerStyle = isIsland
        ? {
            width: '20%',
            minWidth: '120px',
            aspectRatio: '3/2',
            backgroundImage: `url(${CenterWait})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            padding: '0',
            position: 'absolute' as const,
            bottom: '2.5%',
            left: '1.5%',
            borderRadius: '10px',
            overflow: 'hidden',
            zIndex: '3',
            justifySelf: `${gridInfo.localJustifySelf}`,
        }
        : {
            gridColumn: `${gridInfo.localCol} / span ${gridInfo.localColSpan}`,
            gridRow: `${gridInfo.localRow} / span ${gridInfo.localRowSpan}`,
            width: '100%',
            backgroundImage: `url(${CenterWait})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            padding: '0',
            position: 'relative' as const,
            overflow: 'hidden',
            zIndex: '3',
            justifySelf: `${gridInfo.localJustifySelf}`,
        };

    return (
        <div
            id="local-cell"
            style={containerStyle}
        >
            <video
                id="local-cell-video"
                autoPlay={true}
                style={{
                    display: 'block',
                    backgroundImage: `url(${CenterWait})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    objectFit: 'cover',
                    padding: '0',
                    width: '100%',
                    aspectRatio: '16/9',
                }}
                crossOrigin="anonymous"
                ref={localElemRef}
            />
            <audio id="local-cell-audio" className="jitsiTrack" autoPlay={true} />
            {!isMicMuted && (
                <div
                    className="participant-name"
                    style={{
                        maxWidth: '95%',
                        position: 'absolute',
                        top: '3.5%',
                        right: '2%',
                        fontSize: '1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        padding: '0.5rem',
                        borderRadius: '50%',
                    }}
                />
            )}
            {
                <div
                    className="participant-name"
                    style={{
                        maxWidth: '95%',
                        position: 'absolute',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        padding: '0.1rem',
                    }}
                >
                    {centerName}
                </div>
            }
        </div>
    );
};

export default LocalCell;
